// Admin sign-in page — email + password, Arabic RTL, executive-restrained.

import { useState } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

export default function AdminLoginPage() {
  const { user, isLoading, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  const [email,   setEmail]   = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // If already signed in AS admin OR editor, jump to the destination.
  // Regular users are NOT bounced back to /admin/login — the
  // handleSubmit path will have signed them out and shown the
  // role-denied message inline.
  if (!isLoading && user && (user.role === "admin" || user.role === "editor")) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setErrorMsg("يرجى إدخال البريد الإلكتروني وكلمة المرور.");
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u.role !== "admin" && u.role !== "editor") {
        // Non-admin roles must not gain an authenticated session on the
        // admin surface — sign them right back out so the guarded
        // routes remain sealed.
        await logout();
        setErrorMsg("هذا الحساب ليس لديه صلاحية الوصول إلى لوحة الإدارة.");
        return;
      }
      toast.success("تم تسجيل الدخول");
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.status;
      if (status === 429) {
        setErrorMsg("جرى قفل الحساب مؤقتاً بعد محاولات فاشلة. أعد المحاولة بعد قليل.");
      } else if (status === 401) {
        setErrorMsg("بيانات الدخول غير صحيحة.");
      } else {
        setErrorMsg("تعذّر الوصول إلى الخادم. حاول لاحقاً.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50" dir="rtl">
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16">
        <Link
          to="/"
          data-testid="admin-login-back-home"
          className="mb-8 inline-flex items-center gap-1.5 self-start text-[13px] font-medium text-ink-500 hover:text-ink-900"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          العودة إلى الموقع العام
        </Link>

        <Logo variant="full" size="md" />
        <div className="mt-6 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
          Fursalogia · Admin
        </div>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-ink-900">
          تسجيل الدخول إلى لوحة الإدارة
        </h1>
        <p className="mt-2 text-sm text-ink-500">
          هذه المنطقة مخصّصة لفريق التحرير والإدارة. سيُعاد توجيهك إلى الوجهة المطلوبة بعد التحقّق.
        </p>

        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="mt-10 space-y-4" noValidate>
          <div>
            <label htmlFor="email" className="mb-1.5 block text-[12.5px] font-medium text-ink-900">
              البريد الإلكتروني
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              dir="ltr"
              className="w-full rounded-lg border border-surface-200 bg-white px-3.5 py-2.5 text-sm text-ink-900 shadow-sm focus:border-fursa-500 focus:outline-none focus:ring-2 focus:ring-fursa-500/20"
              placeholder="you@fursa.io"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-[12.5px] font-medium text-ink-900">
              كلمة المرور
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              dir="ltr"
              className="w-full rounded-lg border border-surface-200 bg-white px-3.5 py-2.5 text-sm text-ink-900 shadow-sm focus:border-fursa-500 focus:outline-none focus:ring-2 focus:ring-fursa-500/20"
              required
            />
          </div>

          {errorMsg && (
            <div
              data-testid="admin-login-error"
              role="alert"
              className="rounded-lg border border-risk-100 bg-risk-50/60 px-3.5 py-2.5 text-[12.5px] text-risk-800"
            >
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <LogIn className="h-4 w-4" strokeWidth={2} />}
            {submitting ? "جاري التحقّق…" : "تسجيل الدخول"}
          </button>
        </form>

        <p className="mt-6 text-center text-[11px] text-ink-500">
          نسيت كلمة المرور؟ راجع مدير النظام لإعادة تعيينها.
        </p>
      </main>
    </div>
  );
}
